import { SensorFilterParams, SensorStatus } from "../../enums/Sensor";
import { useEffect, useMemo, useState } from "react";

import { IOperationBoxData } from "../../interfaces/Operations";
import Loading from "../../components/Loading/Loading";
import OperationBox from "../../components/OperationBox/OperationBox";
import SensorService from "../../services/SensorService";
import { URLRoutes } from "../../enums/Routing";
import useSnackbar from "../../hooks/useSnackbar";

interface IDashboardData {
  all: number;
  assignedInternal: number;
  atCustomer: number;
  atWarehouse: number;
  inTransit: number;
  pendingSensorAssignment: number;
  pendingShipment: number;
  assignedRemote: number;
  assignedToClinic: number;
  assignedForInClinic: number;
  lost: number;
  damaged: number;
  returnedToWh: number;
}

const OperationsDashboard = () => {
  const { snackbarComponent, setSnackbarError } = useSnackbar();

  const [data, setData] = useState<IDashboardData | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    SensorService.getDashboardData()
      .then(({ data }) => setData(data))
      .catch(() => setSnackbarError())
      .finally(() => setLoading(false));
  }, [setSnackbarError]);

  const firstRowBoxesData = useMemo(
    (): IOperationBoxData[] => [
      {
        title: "New Orders",
        count: data?.pendingSensorAssignment,
        tooltip:
          "All patients that have no sensor assigned yet, but have active orders",
        to: URLRoutes.NewOrders,
        redHeader: true,
      },
      {
        title: "Pending Shipment Info",
        count: data?.pendingShipment,
        tooltip: "All assigned sensors that are missing shipment information",
        to: URLRoutes.SensorsPendingShipment,
        redHeader: true,
      },
      {
        title: "All Sensors",
        count: data?.all,
        to: URLRoutes.Sensors,
      },
    ],
    [data]
  );

  const secondRowBoxesData = useMemo(
    (): IOperationBoxData[] => [
      {
        title: "Warehouse Unassigned",
        count: data?.atWarehouse,
        tooltip: "Unassigned sensors that are currently in warehouse",
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.Unassigned}`,
      },
      {
        title: "Warehouse Assigned",
        count: data?.pendingShipment,
        tooltip: "Assigned sensors that are currently in warehouse",
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.InWarehouse}`,
      },
      {
        title: "In Transit to Customer",
        tooltip: "Sensors that are currently in transit to customer",
        count: data?.inTransit,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.ShippedToCustomer}`,
      },
      {
        title: "At Customer",
        tooltip: "Sensors that are currently at customer",
        count: data?.atCustomer,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.AtCustomer}`,
      },
      {
        title: "Returned to Warehouse",
        tooltip: "Sensors that were returned back to warehouse",
        count: data?.returnedToWh,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.ReturnedToWarehouse}`,
      },
    ],
    [data]
  );

  const thirdRowBoxesData = useMemo(
    (): IOperationBoxData[] => [
      {
        title: "Account On-site Inventory",
        tooltip: "Sensors that are in account on-site inventory",
        count: data?.assignedToClinic,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.AssignedToClinic}`,
      },
      {
        title: "Remote Monitoring",
        tooltip: "Sensors that are currently assigned for remote monitoring and should be tracked via CRM",
        count: data?.assignedRemote,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.AssignedRemote}`,
      },
      {
        title: "Assigned for In Clinic",
        tooltip: "Sensors that are assigned for in clinic use",
        count: data?.assignedForInClinic,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.AssignedForInClinic}`,
      },
    ],
    [data]
  );

  const fourthRowBoxesData = useMemo(
    (): IOperationBoxData[] => [
      {
        title: "Assigned Internal",
        count: data?.assignedInternal,
        tooltip: "Sensors that are assigned internally",
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.AssignedInternal}`,
      },
      {
        title: "Lost",
        tooltip: "Sensors marked as lost",
        count: data?.lost,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.Lost}`,
      },
      {
        title: "Damaged",
        tooltip: "Sensors marked as damaged",
        count: data?.damaged,
        to: `${URLRoutes.Sensors}?${SensorFilterParams.Status}=${SensorStatus.Damaged}`,
      },
    ],
    [data]
  );

  return (
    <div className="h-full flex flex-col gap-4 pr-5 xl:pr-0">
      <h1 className="text-2xl leading-7 font-bold text-cloudBurst">
        Operations
      </h1>

      <div className="relative pb-5 grow h-0 flex flex-col gap-6 overflow-auto md:gap-12">
        {data &&
          [firstRowBoxesData, secondRowBoxesData, thirdRowBoxesData, fourthRowBoxesData].map((boxesData, index) => (
            <div key={index} className="flex flex-wrap gap-6">
              {boxesData.map((data) => (
                <OperationBox key={data.title} {...data} />
              ))}
            </div>
          ))}

        <Loading loading={loading} />
      </div>

      {snackbarComponent}
    </div>
  );
};

export default OperationsDashboard;
